import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, Crosshair, Loader2, Plus, Save, Trash2 } from "lucide-react";

import { api, ApiError, type Camera, type CameraWriteResult } from "../../api";
import { connectDirect, directSupported } from "../../direct";
import { FisheyeView } from "../../components/FisheyeView";
import { Modal } from "../../components/Modal";
import {
  DEFAULT_LENS,
  LENS_MODELS,
  VIEW_PRESETS,
  circleFraction,
  detectCircle,
  isCalibrated,
  samePose,
  type Lens,
  type LensModel,
  type Mount,
  type Pose,
} from "../../fisheye/lens";
import { useRole } from "../../hooks/useRole";
import { humanizeApplyErrors, Note } from "./shared";

const MODEL_LABEL: Record<LensModel, string> = {
  equisolid: "Equisolid",
  equidistant: "Equidistant",
  stereographic: "Stereographic",
  orthographic: "Orthographic",
};

export const MOUNT_LABEL: Record<Mount, string> = {
  ceiling: "Ceiling",
  wall: "Wall",
  floor: "Floor / table",
};

const FREE_START: Pose = { yaw: 0, tilt: 45, fov: 75 };

export type Picture = { source: HTMLImageElement | ImageBitmap; width: number; height: number };
type PictureSource = "recorder" | "live";

/** One frame of the camera's live stream, decoded in the browser at the
 *  stream's own resolution: what calibration uses when there is no recorder
 *  (and a sharper picture than the recorder's snapshot when there is). */
export async function grabLiveFrame(cameraId: string): Promise<Picture> {
  if (!directSupported()) throw new Error("This browser cannot decode the camera's stream.");
  const opened = await api.streams.open({ camera_id: cameraId, kind: "live" });
  const canvas = document.createElement("canvas");
  return new Promise<Picture>((resolve, reject) => {
    let settled = false;
    const conn = connectDirect(opened.session, canvas, {
      live: true,
      bufferMs: 0,
      onFirstFrame: () => {
        if (settled) return;
        settled = true;
        void createImageBitmap(canvas).then(
          (bmp) => {
            conn.close();
            resolve({ source: bmp, width: bmp.width, height: bmp.height });
          },
          (e) => {
            conn.close();
            reject(e);
          },
        );
      },
      onError: (message) => {
        if (settled) return;
        settled = true;
        conn.close();
        reject(new Error(message));
      },
    });
    window.setTimeout(() => {
      if (settled) return;
      settled = true;
      conn.close();
      reject(new Error("The camera sent no picture."));
    }, 15_000);
  });
}

export function recorderSnapshot(cameraId: string): Promise<Picture> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ source: img, width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("The recorder has no picture from this camera right now."));
    img.src = api.recordings.thumbUrl(cameraId, Date.now() - 15_000);
  });
}

/** Fisheye calibration and AI views for one camera.
 *
 *  Calibration fits the image circle on a picture of the camera (a recorder
 *  snapshot, or a live frame for a camera without a recorder); the mount and
 *  projection are set here. Each AI view is a dewarped perspective of the
 *  fisheye that the pipeline analyses as its own AI camera, chosen from a
 *  preset layout or framed in the free look-around.
 */
export function FisheyeDialog({
  camera,
  views,
  focusViewId,
  onClose,
}: {
  camera: Camera;
  views: Camera[];
  focusViewId?: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { role } = useRole();
  const hasRecorder = Boolean(camera.nvr_id);
  const [pictureFrom, setPictureFrom] = useState<PictureSource>(hasRecorder ? "recorder" : "live");
  const [picture, setPicture] = useState<Picture | null>(null);
  const [pictureError, setPictureError] = useState("");
  const [lens, setLens] = useState<Lens>(camera.lens ?? DEFAULT_LENS);
  const [detected, setDetected] = useState("");
  const [editing, setEditing] = useState<string | null>(focusViewId ?? null);
  const [pose, setPose] = useState<Pose>(() => views.find((x) => x.id === focusViewId)?.view ?? FREE_START);
  const [name, setName] = useState(() => {
    const v = views.find((x) => x.id === focusViewId);
    return v ? viewLabel(camera, v) : "";
  });
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  const runDetect = (pic: Picture, base: Lens) => {
    const fit = detectCircle(pic.source, pic.width, pic.height);
    if (!fit || fit.inliers < 0.5) {
      setDetected("No clean image-circle edge was found in this picture.");
      return;
    }
    setLens({ ...base, ...circleFraction(fit, pic.width, pic.height) });
    setDetected(
      `Image circle at ${fit.cx.toFixed(0)}, ${fit.cy.toFixed(0)}, radius ${fit.r.toFixed(0)} px ` +
        `of ${pic.width}×${pic.height} (${(fit.inliers * 100).toFixed(0)}% of edge points).`,
    );
  };

  useEffect(() => {
    let cancelled = false;
    setPicture(null);
    setPictureError("");
    (pictureFrom === "recorder" ? recorderSnapshot(camera.id) : grabLiveFrame(camera.id)).then(
      (pic) => {
        if (cancelled) return;
        setPicture(pic);
        if (!isCalibrated(camera.lens)) runDetect(pic, camera.lens ?? DEFAULT_LENS);
      },
      (e: unknown) => {
        if (!cancelled) setPictureError(e instanceof Error ? e.message : String(e));
      },
    );
    return () => {
      cancelled = true;
    };
    // runDetect only reads the picture it is given.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [camera.id, pictureFrom]);

  const done = (r: CameraWriteResult, text: string) => {
    qc.invalidateQueries({ queryKey: ["cameras"] });
    qc.invalidateQueries({ queryKey: ["camera-capacity"] });
    qc.invalidateQueries({ queryKey: ["monitoring"] });
    setResult(
      r.apply_errors.length
        ? { ok: false, text: `Saved, but not live yet: ${humanizeApplyErrors(r.apply_errors, role)}` }
        : { ok: true, text },
    );
  };
  const failed = (e: unknown) =>
    setResult({ ok: false, text: e instanceof ApiError ? e.message : "The change could not be saved." });

  const saveLens = useMutation({
    mutationFn: (l: Lens) => api.cameras.setLens(camera.id, l),
    onSuccess: (r) => done(r, "Calibration saved. AI views follow it live."),
    onError: failed,
  });
  const addView = useMutation({
    mutationFn: (v: Pose & { name: string }) => api.cameras.addView(camera.id, v),
    onSuccess: (r) => {
      done(r, "AI view added.");
      if (r.camera) setEditing(r.camera.id);
    },
    onError: failed,
  });
  const togglePreset = useMutation({
    mutationFn: async (v: Pose & { name: string }) => {
      const existing = views.find((x) => x.view && samePose(x.view, v));
      return existing ? api.cameras.deleteView(camera.id, existing.id) : api.cameras.addView(camera.id, v);
    },
    onSuccess: (r) => done(r, "AI views updated."),
    onError: failed,
  });
  const editView = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Pose> & { name?: string } }) =>
      api.cameras.editView(camera.id, id, patch),
    onSuccess: (r) => done(r, "AI view updated."),
    onError: failed,
  });
  const deleteView = useMutation({
    mutationFn: (id: string) => api.cameras.deleteView(camera.id, id),
    onSuccess: (r, id) => {
      done(r, "AI view removed.");
      if (editing === id) setEditing(null);
    },
    onError: failed,
  });

  const lensDirty = useMemo(
    () => JSON.stringify(lens) !== JSON.stringify(camera.lens ?? null),
    [lens, camera.lens],
  );
  const calibrated = isCalibrated(camera.lens) && !lensDirty;
  const busy =
    saveLens.isPending || addView.isPending || togglePreset.isPending || editView.isPending || deleteView.isPending;
  const presetPoses = VIEW_PRESETS.flatMap((p) => p.views(lens));
  const customViews = views.filter((v) => !v.view || !presetPoses.some((p) => samePose(p, v.view!)));

  const choose = (v: Camera) => {
    setEditing(v.id);
    if (v.view) setPose({ ...v.view });
    setName(viewLabel(camera, v));
  };

  return (
    <Modal onClose={onClose} labelledBy="fisheye-title" panelClassName="w-full max-w-5xl">
      <div className="flex flex-col gap-4">
        <div>
          <h2 id="fisheye-title" className="text-lg font-semibold">
            Fisheye · {camera.name || camera.id}
          </h2>
          <p className="text-sm text-bf-muted">
            Each AI view is a dewarped perspective of this fisheye, analysed as its own AI camera
            and counted against the AI camera limit.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
          <div className="flex flex-col gap-2">
            <div className="relative aspect-video overflow-hidden rounded-lg border border-white/10 bg-black">
              {pictureError ? (
                <div className="absolute inset-0 grid place-items-center p-4 text-center text-sm text-bf-muted">
                  {pictureError}
                </div>
              ) : !picture ? (
                <div className="absolute inset-0 grid place-items-center text-bf-muted">
                  <Loader2 className="animate-spin" size={20} />
                </div>
              ) : isCalibrated(lens) ? (
                <FisheyeView
                  lens={lens}
                  image={picture.source}
                  views={[pose]}
                  interactive
                  onChange={(v) => setPose(v[0])}
                />
              ) : (
                <div className="absolute inset-0 grid place-items-center p-4 text-center text-sm text-bf-muted">
                  {detected || "Finding the image circle…"}
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs text-bf-muted">
              <span>Drag to look around, scroll to zoom, click the small fisheye to aim there.</span>
              <span className="ml-auto font-mono">
                yaw {pose.yaw.toFixed(1)}° · tilt {pose.tilt.toFixed(1)}° · fov {pose.fov.toFixed(0)}°
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                className="bf-input min-w-[180px] flex-1"
                placeholder="View name, e.g. Entrance"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={60}
              />
              {editing ? (
                <>
                  <button
                    className="bf-button disabled:opacity-40"
                    disabled={busy || !name.trim()}
                    onClick={() => editView.mutate({ id: editing, patch: { ...pose, name: name.trim() } })}
                  >
                    <Save size={15} className="mr-2" /> Update view
                  </button>
                  <button
                    className="bf-button bg-white/10 bg-none"
                    onClick={() => {
                      setEditing(null);
                      setName("");
                    }}
                  >
                    New view
                  </button>
                </>
              ) : (
                <button
                  className="bf-button disabled:opacity-40"
                  disabled={busy || !calibrated || !name.trim()}
                  title={calibrated ? undefined : "Save the calibration first"}
                  onClick={() => addView.mutate({ ...pose, name: name.trim() })}
                >
                  <Plus size={15} className="mr-2" /> Save as AI view
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <section className="flex flex-col gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-bf-muted">Calibration</h3>
              <div className="flex overflow-hidden rounded-md border border-white/10 text-xs">
                {hasRecorder && (
                  <button
                    className={`flex-1 px-2 py-1.5 ${pictureFrom === "recorder" ? "bg-bf-violet/30 text-white" : "text-bf-muted hover:text-white"}`}
                    onClick={() => setPictureFrom("recorder")}
                  >
                    Recorder snapshot
                  </button>
                )}
                <button
                  className={`flex-1 px-2 py-1.5 ${pictureFrom === "live" ? "bg-bf-violet/30 text-white" : "text-bf-muted hover:text-white"}`}
                  onClick={() => setPictureFrom("live")}
                >
                  Live frame
                </button>
              </div>
              <p className="text-xs text-bf-muted">
                {pictureFrom === "recorder"
                  ? "A still from the recorder. Quick, at the recorder's snapshot size."
                  : "One frame of the camera's live stream at full stream resolution, decoded in this browser."}
              </p>
              <label className="flex items-center justify-between gap-2 text-sm">
                Mount
                <select
                  className="bf-input w-40"
                  value={lens.mount}
                  onChange={(e) => setLens({ ...lens, mount: e.target.value as Mount })}
                >
                  {(Object.keys(MOUNT_LABEL) as Mount[]).map((m) => (
                    <option key={m} value={m}>
                      {MOUNT_LABEL[m]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center justify-between gap-2 text-sm">
                Projection
                <select
                  className="bf-input w-40"
                  value={lens.model}
                  onChange={(e) => setLens({ ...lens, model: e.target.value as LensModel })}
                >
                  {LENS_MODELS.map((m) => (
                    <option key={m} value={m}>
                      {MODEL_LABEL[m]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex items-center justify-between gap-2 text-sm">
                <span>
                  Edge angle <span className="font-mono text-bf-muted">{lens.theta_max_deg}°</span>
                </span>
                <input
                  type="range"
                  min={80}
                  max={110}
                  step={0.5}
                  className="w-40"
                  value={lens.theta_max_deg}
                  onChange={(e) => setLens({ ...lens, theta_max_deg: Number(e.target.value) })}
                />
              </label>
              <label className="flex items-center justify-between gap-2 text-sm">
                <span>
                  Roll <span className="font-mono text-bf-muted">{lens.roll_deg}°</span>
                </span>
                <input
                  type="range"
                  min={-180}
                  max={180}
                  step={1}
                  className="w-40"
                  value={lens.roll_deg}
                  onChange={(e) => setLens({ ...lens, roll_deg: Number(e.target.value) })}
                />
              </label>
              {detected && <p className="text-xs text-bf-muted">{detected}</p>}
              <div className="flex gap-2">
                <button
                  className="bf-button bg-white/10 bg-none disabled:opacity-40"
                  disabled={!picture}
                  onClick={() => picture && runDetect(picture, lens)}
                >
                  <Crosshair size={15} className="mr-2" /> Find circle
                </button>
                <button
                  className="bf-button disabled:opacity-40"
                  disabled={!isCalibrated(lens) || !lensDirty || saveLens.isPending}
                  onClick={() => saveLens.mutate(lens)}
                >
                  <Save size={15} className="mr-2" /> Save calibration
                </button>
              </div>
              {calibrated && (
                <Note tone="info">
                  Once calibrated, set the camera itself to fisheye-only mode (camera-side PTZ or
                  panorama views off). Those views make the camera stream its fisheye at reduced
                  resolution; in fisheye-only mode it streams the full picture, and this calibration
                  carries over.
                </Note>
              )}
            </section>

            <section className="flex flex-col gap-2">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-bf-muted">AI views</h3>
              {!calibrated && views.length > 0 && (
                <p className="text-xs text-amber-300">
                  These views start being analysed once the calibration is saved.
                </p>
              )}
              {VIEW_PRESETS.map((preset) => (
                <div key={preset.id} className="flex flex-col gap-1">
                  <span className="text-xs text-bf-muted">{preset.label}</span>
                  <div className="flex flex-wrap gap-1">
                    {preset.views(lens).map((v) => {
                      const on = views.some((x) => x.view && samePose(x.view, v));
                      return (
                        <button
                          key={v.name}
                          className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs disabled:opacity-40 ${
                            on ? "border-bf-magenta/60 bg-bf-magenta/15 text-white" : "border-white/10 text-bf-muted hover:text-white"
                          }`}
                          disabled={busy}
                          onClick={() => togglePreset.mutate(v)}
                          aria-pressed={on}
                        >
                          {on ? <Check size={12} /> : <Plus size={12} />} {v.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <span className="pt-1 text-xs text-bf-muted">Custom views</span>
              {customViews.length === 0 ? (
                <p className="text-xs text-bf-muted">
                  Frame one in the look-around, name it and save it as an AI view.
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {customViews.map((v) => (
                    <li
                      key={v.id}
                      className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm ${
                        editing === v.id ? "border-bf-magenta/60 bg-bf-magenta/10" : "border-white/10"
                      }`}
                    >
                      <button className="flex-1 truncate text-left" onClick={() => choose(v)}>
                        {viewLabel(camera, v)}
                        {v.view && (
                          <span className="ml-2 font-mono text-[11px] text-bf-muted">
                            {v.view.yaw.toFixed(0)}° / {v.view.tilt.toFixed(0)}° / {v.view.fov.toFixed(0)}°
                          </span>
                        )}
                      </button>
                      <button
                        className="text-bf-muted hover:text-red-400"
                        aria-label={`Remove ${viewLabel(camera, v)}`}
                        onClick={() => deleteView.mutate(v.id)}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        </div>

        {result && <Note tone={result.ok ? "success" : "warning"}>{result.text}</Note>}

        <div className="flex justify-end">
          <button className="bf-button bg-white/10 bg-none" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}

/** The part of a view's camera name after "<fisheye name> · ". */
export function viewLabel(parent: Camera, view: Camera): string {
  const prefix = `${(parent.name || parent.id).trim()} · `;
  return view.name.startsWith(prefix) ? view.name.slice(prefix.length) : view.name;
}
